/**
 * CHIP UX analytics client — batches chatbot UI events to the server's
 * first-party pipeline (POST /chip-analytics/events).
 *
 * Deliberately independent of utilities/analyticsService.js (the OpenPanel
 * tracker): CHIP events must never leak into the legacy pipeline through its
 * browser fallback, and this client must never block or break the chat UI —
 * every failure path drops silently. Identity is attached server-side from the
 * JWT; this client only ships event names, timestamps, the CHIP conversation id
 * and small props.
 */

import { API_BASE_URL } from "../../config";

const MAX_BATCH = 25;
const FLUSH_AFTER_COUNT = 10;
const FLUSH_AFTER_MS = 3000;
// navigator.sendBeacon payloads over ~64KB are rejected outright; stay under it.
const BEACON_MAX_BYTES = 60000;

let queue = [];
let flushTimer = null;

/**
 * Auth token for the events endpoint, mirroring how chatBotService's stream call
 * reads it. Null (logged out) disables sending entirely — chat is auth-gated, so
 * this is a belt-and-braces no-op path, not a real flow.
 * @returns {?string} bearer token or null
 */
const getToken = () => {
  try {
    return localStorage.getItem("token") || null;
  } catch (_error) {
    return null;
  }
};

/**
 * Send one batch with fetch keepalive. Fire-and-forget: a non-2xx or network
 * error drops the batch (server-side events carry the business KPIs; these are
 * UX signals, so durability is deliberately not worth retry complexity here).
 * @param {object[]} events batch to send (≤25)
 * @returns {void}
 */
const postBatch = (events) => {
  const token = getToken();
  if (!token || !API_BASE_URL || events.length === 0) return;
  try {
    fetch(`${API_BASE_URL}/chip-analytics/events`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch (_error) {
    // never throw into the UI
  }
};

/**
 * Drain the queue in ≤25-event batches.
 * @returns {void}
 */
export const flushChipUx = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (queue.length > 0) {
    postBatch(queue.splice(0, MAX_BATCH));
  }
};

/**
 * Last-gasp flush on tab hide/close. Uses fetch with keepalive rather than
 * navigator.sendBeacon: the events endpoint requires an Authorization header,
 * which a beacon cannot carry, and keepalive fetches survive unload in the
 * browsers the portal supports. Payload capped by dropping the OLDEST events
 * beyond the byte budget — the most recent interaction is the one worth keeping.
 * @returns {void}
 */
const flushOnHide = () => {
  if (queue.length === 0) return;
  let batch = queue.splice(0, MAX_BATCH);
  try {
    let body = JSON.stringify({ events: batch });
    while (body.length > BEACON_MAX_BYTES && batch.length > 1) {
      batch = batch.slice(1);
      body = JSON.stringify({ events: batch });
    }
    postBatch(batch);
  } catch (_error) {
    // drop silently
  }
};

/**
 * Queue one CHIP UX event. Safe to call unconditionally from render handlers:
 * never throws, never blocks, no-ops entirely when the event can't be sent.
 * @param {string} eventName portal-cataloged chip_* event name (the server
 *   rejects anything outside its portal allowlist, so a typo is dropped there)
 * @param {object} [props] small JSON-safe payload (no free text beyond short labels)
 * @param {?string} [chipSessionId] the CHIP conversation id (ChatBot's conversationId)
 * @returns {void}
 */
export const trackChipUx = (eventName, props = {}, chipSessionId = null) => {
  try {
    // Site attribution: the server resolves site_number -> site_id at ingest,
    // so UX events stay filterable by bakery location like server events.
    let siteNumber = null;
    try {
      siteNumber = localStorage.getItem("selectedSite") || null;
    } catch (_error) { /* storage unavailable: send without site */ }
    queue.push({
      event_name: String(eventName || ""),
      occurred_at: new Date().toISOString(),
      chip_session_id: chipSessionId || null,
      site_number: siteNumber,
      props: props && typeof props === "object" ? props : {},
    });
    if (queue.length > 200) queue.shift(); // runaway guard: cap memory, drop oldest
    if (queue.length >= FLUSH_AFTER_COUNT) {
      flushChipUx();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flushChipUx, FLUSH_AFTER_MS);
    }
  } catch (_error) {
    // never throw into the UI
  }
};

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnHide();
  });
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flushOnHide);
}
